import { useMemo, useState } from "react";
import { Loader2, Send, Sparkles } from "lucide-react";
import { useRealtimeRun } from "@trigger.dev/react-hooks";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ApiError, triggerTask, type TriggerResponse } from "@/lib/api";

const SUGGESTIONS = [
  "Summarize what Job Goblin can do in plain English.",
  "Draft a friendly follow-up email after a job interview.",
  "Turn this messy note into a concise task list.",
  "Give me three ideas for using a private local AI runner.",
];

export function DemoPage() {
  const [message, setMessage] = useState("");
  const [activeRun, setActiveRun] = useState<TriggerResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(nextMessage = message) {
    const trimmed = nextMessage.trim();
    if (!trimmed) return;

    setMessage(trimmed);
    setActiveRun(null);
    setError(null);
    setBusy(true);
    try {
      const run = await triggerTask({
        taskId: "local-chat",
        payload: {
          message: trimmed,
          model: "qwen",
          system:
            "You are Job Goblin's demo assistant. Be practical, concise, and friendly. Avoid exposing internal implementation details unless asked.",
          temperature: 0.3,
        },
      });
      setActiveRun(run);
    } catch (err) {
      if (err instanceof ApiError) {
        const reason =
          (err.body as { reason?: string; error?: string })?.reason ??
          (err.body as { reason?: string; error?: string })?.error ??
          `${err.status}`;
        setError(`Demo request failed: ${reason}`);
      } else {
        setError(err instanceof Error ? err.message : "Demo request failed");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-3.5rem)] flex-col">
      <main className="flex flex-1 flex-col px-4 py-8">
        <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center gap-7">
          <div className="space-y-4 text-center">
            <Badge variant="accent" className="mx-auto">
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Local Qwen demo
            </Badge>
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                What can I help with?
              </h1>
              <p className="mx-auto max-w-xl text-sm text-muted-foreground">
                Ask one question and Job Goblin will route it to the private
                Ollama runner.
              </p>
            </div>
          </div>

          <form
            className="rounded-lg border bg-card p-2 shadow-sm"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Message Job Goblin"
              rows={3}
              className="min-h-[104px] w-full resize-none bg-transparent px-3 py-3 text-base outline-none placeholder:text-muted-foreground"
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void submit();
                }
              }}
            />
            <div className="flex items-center justify-between gap-3 border-t px-2 pt-2">
              <div className="text-xs text-muted-foreground">Qwen on Ollama</div>
              <Button type="submit" size="sm" disabled={busy || !message.trim()}>
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                <span className="sr-only">Send</span>
              </Button>
            </div>
          </form>

          <div className="grid gap-2 sm:grid-cols-2">
            {SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                className="rounded-md border bg-background px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:border-teal-500/50 hover:bg-teal-500/10 hover:text-foreground"
                onClick={() => void submit(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {activeRun && (
            <DemoRun runId={activeRun.runId} accessToken={activeRun.publicAccessToken} />
          )}
        </div>
      </main>
    </div>
  );
}

function DemoRun({ runId, accessToken }: { runId: string; accessToken: string }) {
  const { run, error } = useRealtimeRun(runId, { accessToken });
  const response = useMemo(() => {
    const output = run?.output as { response?: unknown } | undefined;
    return typeof output?.response === "string" ? output.response.trim() : "";
  }, [run?.output]);

  return (
    <section className="rounded-lg border bg-card p-4">
      {!run && !error && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Waiting for the runner...
        </div>
      )}
      {error && (
        <div className="text-sm text-destructive">{error.message}</div>
      )}
      {run && !response && !run.error && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Thinking...
        </div>
      )}
      {response && (
        <div className="whitespace-pre-wrap text-sm leading-6 text-card-foreground">
          {response}
        </div>
      )}
      {run?.error && (
        <pre className="overflow-auto rounded-md border border-destructive/40 bg-destructive/10 p-3 font-mono text-xs text-destructive">
          {JSON.stringify(run.error, null, 2)}
        </pre>
      )}
    </section>
  );
}
